import { IntelliAuthLogo } from '@intelliauth/ui'
import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

const TRUST_POINTS = [
  'Risk-based attack protection',
  'Provable multi-tenant isolation',
  'SOC 2 Type II · ISO 27001 · GDPR & DPDP',
]

interface AuthLayoutProps {
  children: ReactNode
}

/**
 * Split-screen shell for the CP auth pages (sign-in + register).
 *
 * LEFT panel (>= lg only): a branded navy/blue gradient rail carrying the
 * logo, a confident headline, a one-line subhead, and three trust points.
 * RIGHT panel: the page's own form/stepper, vertically centred on the light
 * surface at a comfortable reading width.
 *
 * On mobile the left panel is hidden and the form becomes a clean centred
 * column. This component owns ONLY the outer chrome — the caller supplies
 * the entire form via `children`, so form logic and copy stay untouched.
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-ia-page">
      {/* ── LEFT: brand panel (hidden below lg) ─────────────────── */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-primary via-primary-hover to-primary-active lg:flex lg:w-[46%] lg:max-w-[640px] lg:flex-col lg:justify-between lg:p-12 xl:p-16">
        {/* Subtle dot-grid accent */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:radial-gradient(circle,#ffffff_1px,transparent_1px)] [background-size:22px_22px]"
        />

        <Link to="/" className="relative inline-flex">
          <IntelliAuthLogo size="lg" variant="light" />
        </Link>

        <div className="relative max-w-md">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-primary-foreground xl:text-4xl">
            The customer identity platform that stops attacks
          </h1>
          <p className="mt-4 text-base leading-relaxed text-primary-foreground/70">
            Adaptive, risk-aware authentication with tenant isolation you can prove — not just
            promise.
          </p>

          <ul className="mt-10 space-y-4">
            {TRUST_POINTS.map((point) => (
              <li key={point} className="flex items-center gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cta/20 text-cta">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-sm font-medium text-primary-foreground/90">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-primary-foreground/50">
          &copy; {new Date().getFullYear()} IntelliAuth
        </p>
      </aside>

      {/* ── RIGHT: form column ──────────────────────────────────── */}
      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-12">
        <div className="w-full max-w-md animate-fade-in">{children}</div>
      </main>
    </div>
  )
}
